import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import Lottie from "lottie-react";
import "./App.css";

// Animation variants for consistent motion design
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Custom hook for scroll animations
const useScrollAnimation = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.1, once: true });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return [ref, controls];
};

// Hero Section Component
const HeroSection = () => {
  const [affiliateAnimation, setAffiliateAnimation] = useState(null);

  useEffect(() => {
    const loadAnimation = async () => {
      try {
        const response = await fetch('/animations/Affiliate.json');
        if (response.ok) {
          const data = await response.json();
          setAffiliateAnimation(data);
        }
      } catch (error) {
        console.error('Error loading animation:', error);
      }
    };
    
    loadAnimation();
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-deep-jungle">
      {/* SVG Background Illustration */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('https://agents.beunframed.com/wp-content/uploads/2025/07/Untitled-design-30.svg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          opacity: 1
        }}
      />
      
      {/* Content - Two Columns */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 pt-20 md:pt-0 grid md:grid-cols-2 gap-12 items-center">
        {/* Left Column - Content */}
        <div className="text-left text-white">
          <motion.h1 
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="font-clash text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
          >
            We Don't Do Fluff.<br />
            <span className="text-ogre-odor">We Do Results.</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-geist text-lg md:text-xl mb-8 text-gray-200 max-w-2xl"
          >
            Performance-focused affiliate program for publishers who want to scale — not waste time.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <a 
              href="https://calendar.app.google/ptuaUGRPXkwozMAYA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-ogre-odor hover:bg-ogre-odor-dark text-white font-geist font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-premium-orange hover:shadow-2xl"
            >
              Schedule a Call
            </a>
          </motion.div>
        </div>

        {/* Right Column - Lottie Animation */}
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="flex justify-center"
        >
          <div className="w-full max-w-lg h-96">
            {affiliateAnimation ? (
              <Lottie 
                animationData={affiliateAnimation}
                loop={true}
                autoplay={true}
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              <div className="w-full h-full bg-deep-jungle-light/20 rounded-2xl flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p className="font-geist">Loading animation...</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-bounce"></div>
        </div>
      </motion.div>
    </section>
  );
};

// About Section Component
const AboutSection = () => {
  const [ref, controls] = useScrollAnimation();
  const [welcomeAnimation, setWelcomeAnimation] = useState(null);

  useEffect(() => {
    const loadAnimation = async () => {
      try {
        const response = await fetch('/animations/Welcome.json');
        if (response.ok) {
          const data = await response.json();
          setWelcomeAnimation(data);
        }
      } catch (error) {
        console.error('Error loading welcome animation:', error);
      }
    };
    
    loadAnimation();
  }, []);
  
  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp} className="flex justify-center">
            <div className="w-full max-w-md h-96">
              {welcomeAnimation ? (
                <Lottie 
                  animationData={welcomeAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: '100%', height: '100%' }}
                />
              ) : (
                <div className="w-full h-full bg-deep-jungle-light/10 rounded-2xl flex items-center justify-center">
                  <div className="text-deep-jungle text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-deep-jungle mx-auto mb-4"></div>
                    <p className="font-geist">Loading animation...</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
          
          <motion.div variants={fadeInUp} className="space-y-6">
            <h2 className="font-clash text-4xl md:text-5xl font-bold text-deep-jungle mb-6">
              What We Do
            </h2>
            <p className="font-geist text-lg text-gray-700 leading-relaxed">
              Unframed is a private affiliate program built for publishers who drive real traffic and want real results.
            </p>
            <p className="font-geist text-lg text-gray-700 leading-relaxed">
              No generic networks, no slow replies, no shady offers.
            </p>
            <p className="font-geist text-lg text-gray-700 leading-relaxed">
              You get a dedicated affiliate growth manager who helps you optimize your traffic, improve your funnels, and scale your revenue.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Partner Section Component
const WhyPartnerSection = () => {
  const [ref, controls] = useScrollAnimation();
  
  const benefits = [
    {
      icon: "🚀",
      title: "High-converting offers that actually perform",
      description: "Get access to tested, proven offers that convert"
    },
    {
      icon: "⚡",
      title: "Fast setup, no hand-holding needed",
      description: "Quick onboarding for experienced publishers"
    },
    {
      icon: "💬",
      title: "Direct access to your growth manager",
      description: "No middlemen, direct communication channel"
    },
    {
      icon: "📊",
      title: "Help with creatives, angles, and optimization",
      description: "Strategic support for maximum performance"
    },
    {
      icon: "🧠",
      title: "Strategy support to scale what's working",
      description: "Expert guidance to amplify your success"
    },
    {
      icon: "💰",
      title: "Competitive payouts and timely payments",
      description: "Fair compensation with reliable payment schedules"
    }
  ];
  
  return (
    <section ref={ref} className="py-20 bg-deep-jungle-light/30">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-clash text-4xl md:text-5xl font-bold text-deep-jungle mb-12"
          >
            Why Work With Us?
          </motion.h2>
        </motion.div>
        
        <motion.div
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={scaleIn}
              className="bg-white p-8 rounded-2xl shadow-premium hover:shadow-premium-orange transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="text-4xl mb-4">{benefit.icon}</div>
              <h3 className="font-clash text-xl font-semibold text-deep-jungle mb-3">
                {benefit.title}
              </h3>
              <p className="font-geist text-gray-600">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section Component
const HowItWorksSection = () => {
  const [ref, controls] = useScrollAnimation();
  
  const steps = [
    {
      number: "01",
      title: "Reach Out",
      description: "Schedule a call. No strings, just a quick chat."
    },
    {
      number: "02", 
      title: "Get Set Up",
      description: "We move fast. You'll be live in no time."
    },
    {
      number: "03",
      title: "Start Scaling", 
      description: "Work with your manager to grow results. No fluff, no fake promises."
    }
  ];
  
  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-clash text-4xl md:text-5xl font-bold text-deep-jungle mb-6"
          >
            How It Works
            <span className="block text-2xl font-geist font-normal text-gray-600 mt-2">
              (Simple as it Should Be)
            </span>
          </motion.h2>
        </motion.div>
        
        <motion.div
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={scaleIn}
              className="text-center relative"
            >
              <div className="glass-dark text-white w-16 h-16 rounded-full flex items-center justify-center font-clash font-bold text-xl mx-auto mb-6">
                {step.number}
              </div>
              <h3 className="font-clash text-2xl font-semibold text-deep-jungle mb-4">
                {step.title}
              </h3>
              <p className="font-geist text-gray-600 leading-relaxed">
                {step.description}
              </p>
              
              {/* Connection line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-deep-jungle to-ogre-odor transform translate-x-8"></div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Invite Only Section Component
const InviteOnlySection = () => {
  const [ref, controls] = useScrollAnimation();
  const [inviteAnimation, setInviteAnimation] = useState(null);

  useEffect(() => {
    const loadAnimation = async () => {
      try {
        const response = await fetch('/animations/nvite.json');
        if (response.ok) {
          const data = await response.json();
          setInviteAnimation(data);
        }
      } catch (error) {
        console.error('Error loading invite animation:', error);
      }
    };
    
    loadAnimation();
  }, []);
  
  return (
    <section ref={ref} className="py-20 bg-deep-jungle text-white">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp} className="space-y-6">
            <h2 className="font-clash text-4xl md:text-5xl font-bold mb-6">
              Why Invite-Only?
            </h2>
            <p className="font-geist text-xl leading-relaxed text-gray-200">
              We only work with partners we believe in — and who believe in performance.
            </p>
            <p className="font-geist text-xl leading-relaxed text-gray-200">
              If you're serious about growing and don't need hand-holding, you're our kind of publisher.
            </p>
          </motion.div>
          
          <motion.div variants={fadeInUp} className="flex justify-center">
            <div className="w-full max-w-md h-96">
              {inviteAnimation ? (
                <Lottie 
                  animationData={inviteAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: '100%', height: '100%' }}
                />
              ) : (
                <div className="w-full h-full bg-deep-jungle-light/20 rounded-2xl flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="font-geist">Loading animation...</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// AWE Budapest Section Component
const AWEBudapestSection = () => {
  const [ref, controls] = useScrollAnimation();
  
  return (
    <section ref={ref} className="py-20 bg-ogre-odor-light">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp}>
            <img 
              src="https://agents.beunframed.com/wp-content/uploads/2025/07/seth-fogelman-Dzv_m1LhcHc-unsplash.webp"
              alt="Budapest Affiliate World Europe"
              className="rounded-2xl shadow-premium w-full h-96 object-cover"
            />
          </motion.div>
          
          <motion.div variants={fadeInUp} className="space-y-6">
            <h2 className="font-clash text-4xl md:text-5xl font-bold text-deep-jungle mb-6">
              In Budapest for Affiliate World Europe?
            </h2>
            <p className="font-geist text-xl text-gray-700 leading-relaxed">
              We'll be there. If you're attending and want to talk shop — traffic, strategy, scaling — let's meet.
            </p>
            <a 
              href="https://calendar.app.google/ptuaUGRPXkwozMAYA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-deep-jungle hover:bg-ogre-odor text-white font-geist font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-premium"
            >
              Book a Meeting for AWE
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Final CTA Section Component
const FinalCTASection = () => {
  const [ref, controls] = useScrollAnimation();
  
  return (
    <section ref={ref} className="py-20 bg-deep-jungle text-white">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <motion.div
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="space-y-8"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-clash text-4xl md:text-6xl font-bold mb-6"
          >
            Ready to Make It Work?
          </motion.h2>
          
          <motion.div variants={fadeInUp} className="space-y-4">
            <p className="font-geist text-2xl text-gray-200">
              We don't have a sign-up form.
            </p>
            <p className="font-geist text-2xl text-gray-200">
              We'd rather talk to you first.
            </p>
            <p className="font-geist text-xl text-gray-300">
              If you've got traffic and want to scale, book a quick call.
            </p>
          </motion.div>
          
          <motion.div variants={scaleIn}>
            <a 
              href="https://calendar.app.google/ptuaUGRPXkwozMAYA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-ogre-odor hover:bg-ogre-odor-dark text-white font-geist font-semibold px-12 py-6 rounded-xl text-xl transition-all duration-300 transform hover:scale-105 shadow-premium-orange hover:animate-glow"
            >
              Book a Call
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Header Component
const Header = () => {
  const [isInHero, setIsInHero] = useState(true);
  
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.querySelector('section'); // First section is hero
      if (heroSection) {
        const heroHeight = heroSection.offsetHeight;
        const scrollPosition = window.scrollY;
        
        // If scroll position is less than 80% of hero height, we're still in hero
        setIsInHero(scrollPosition < heroHeight * 0.8);
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);
    
    // Initial check
    handleScroll();
    
    // Cleanup
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header 
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="fixed top-0 left-0 right-0 z-50 glass backdrop-blur-md"
    >
      <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
        <div className="font-clash text-2xl font-bold text-deep-jungle">
          <img 
            src={isInHero 
              ? "https://agents.beunframed.com/wp-content/uploads/2025/07/Full-Logo-white-SVG.svg"
              : "https://agents.beunframed.com/wp-content/uploads/2025/07/Full-Logo-white-SVG-2.svg"
            }
            alt="Unframed Logo"
            className="h-8 transition-opacity duration-300"
          />
        </div>
        
        <a 
          href="https://calendar.app.google/ptuaUGRPXkwozMAYA"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-ogre-odor hover:bg-ogre-odor-dark text-white font-geist font-semibold px-6 py-2 rounded-lg transition-all duration-300 text-sm"
        >
          Schedule Call
        </a>
      </div>
    </motion.header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-deep-jungle-dark text-white py-8">
      <div className="max-w-6xl mx-auto px-4 text-center">
        <div className="mb-6">
          <img 
            src="https://agents.beunframed.com/wp-content/uploads/2025/07/Full-Logo-white-SVG.svg"
            alt="Unframed Logo"
            className="h-8 mx-auto mb-4"
          />
        </div>
        <p className="font-geist text-gray-400">
          © 2025 Unframed. Performance-focused affiliate program.
        </p>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Header />
      <HeroSection />
      <AboutSection />
      <WhyPartnerSection />
      <HowItWorksSection />
      <InviteOnlySection />
      <AWEBudapestSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}

export default App;